import { isV4PoolId } from "@/lib/pool-address";
import { getUniswapNetworkSlug } from "@/lib/pools/chains";

export { getUniswapNetworkSlug, isV4PoolId };

export function inferPoolVersion(poolId: string, protocol?: string | null): "V3" | "V4" {
	// Address shape wins — a bytes32 id is always V4 even if a caller passes version=V3.
	if (isV4PoolId(poolId)) return "V4";
	if (protocol?.toUpperCase() === "V4") return "V4";
	if (protocol?.toUpperCase() === "V3") return "V3";
	return "V3";
}

/**
 * Generates a Uniswap pool URL
 */
export function getUniswapPoolUrl(network: string, poolAddress: string): string {
	const networkSlug = getUniswapNetworkSlug(network);
	return `https://app.uniswap.org/explore/pools/${networkSlug}/${poolAddress}`;
}

/**
 * Generates an internal pool analysis route URL (matching Uniswap's pattern)
 */
export function getPoolAnalysisUrl(network: string, poolAddress: string): string {
	const networkSlug = getUniswapNetworkSlug(network);
	return `/pools/${networkSlug}/${poolAddress}`;
}

/** Path pattern for pool detail: /pools/ethereum/0x123 */
export const POOL_PATH_REGEX = /^\/pools\/[^/]+\/0x[a-fA-F0-9]+$/;

/** Parse pool path, returns { network, pool } or null */
export function parsePoolPath(path: string): { network: string; pool: string } | null {
	const m = path.match(/^\/pools\/([^/]+)\/(0x[a-fA-F0-9]+)$/);
	return m ? { network: m[1], pool: m[2] } : null;
}

/** For sidebar/overlay mode: /pools?analysisPool=0x123&analysisNetwork=ethereum (avoids collision with nuqs filter param "network") */
export function getPoolAnalysisSearchUrl(network: string, poolAddress: string): string {
	const networkSlug = getUniswapNetworkSlug(network);
	const params = new URLSearchParams({
		analysisPool: poolAddress,
		analysisNetwork: networkSlug,
	});
	return `/pools?${params.toString()}`;
}

/** Same as above but preserves existing search params (filters, pair, etc.) to avoid table re-renders */
export function getPoolAnalysisUrlPreservingParams(
	network: string,
	poolAddress: string,
	currentHref: string,
): string {
	const networkSlug = getUniswapNetworkSlug(network);
	const url = new URL(
		currentHref,
		typeof window !== "undefined" ? window.location.origin : "http://localhost",
	);
	url.searchParams.set("analysisPool", poolAddress);
	url.searchParams.set("analysisNetwork", networkSlug);
	return url.pathname + url.search;
}

/** Uniswap V3 fee tier (raw) → tickSpacing */
const FEE_TICK_SPACING: Record<number, number> = {
	100: 1,
	500: 10,
	3000: 60,
	10000: 200,
};

/** Parse fee tier string (e.g. "0.3%", "0.05%") to raw fee amount */
export function parseFeeTierToRaw(feeTier: string): number | null {
	const pct = parseFloat(feeTier.replace("%", "").trim());
	if (!Number.isFinite(pct)) return null;
	const raw = Math.round(pct * 1e4); // 0.3% → 3000
	return raw in FEE_TICK_SPACING ? raw : null;
}

/** Tick spacing for a pool fee tier (Uniswap V3/V4). Defaults to 60 when unknown. */
export function getTickSpacingFromFeeTier(feeTier: string): number {
	const raw = parseFeeTierToRaw(feeTier);
	if (raw == null) return 60;
	return FEE_TICK_SPACING[raw] ?? 60;
}

export interface UniswapCreatePositionParams {
	token0Address: string;
	token1Address: string;
	chain: string;
	feeTier: string;
	version?: "V3" | "V4";
}

/** @deprecated Prefer UniswapCreatePositionParams */
export type UniswapV4CreatePositionParams = Omit<UniswapCreatePositionParams, "version">;

function buildCreatePositionSearchParams(
	params: Pick<
		UniswapCreatePositionParams,
		"token0Address" | "token1Address" | "chain" | "feeTier"
	>,
): URLSearchParams | null {
	const { token0Address, token1Address, chain, feeTier } = params;
	if (
		!token0Address ||
		!token1Address ||
		!/^0x[a-fA-F0-9]{40}$/.test(token0Address) ||
		!/^0x[a-fA-F0-9]{40}$/.test(token1Address)
	) {
		return null;
	}
	const feeAmount = parseFeeTierToRaw(feeTier);
	if (feeAmount == null) return null;

	const chainSlug = getUniswapNetworkSlug(chain);
	const tickSpacing = FEE_TICK_SPACING[feeAmount as keyof typeof FEE_TICK_SPACING] ?? 60;

	const fee = JSON.stringify({ feeAmount, tickSpacing, isDynamic: false });
	const priceRangeState = JSON.stringify({
		priceInverted: false,
		fullRange: false,
		initialPrice: "",
		inputMode: "price",
	});
	const depositState = JSON.stringify({ exactField: "TOKEN0", exactAmounts: {} });

	return new URLSearchParams({
		currencyA: token0Address,
		currencyB: token1Address,
		chain: chainSlug,
		fee,
		hook: "undefined",
		priceRangeState,
		depositState,
	});
}

/**
 * Generates Uniswap V3 create position URL
 * https://app.uniswap.org/positions/create/v3?currencyA=...&currencyB=...&chain=...&fee=...
 */
export function getUniswapV3CreatePositionUrl(params: UniswapCreatePositionParams): string | null {
	const search = buildCreatePositionSearchParams(params);
	if (!search) return null;
	return `https://app.uniswap.org/positions/create/v3?${search.toString()}`;
}

/**
 * Generates Uniswap V4 create position URL
 * https://app.uniswap.org/positions/create/v4?currencyA=...&currencyB=...&chain=...&fee=...
 */
export function getUniswapV4CreatePositionUrl(params: UniswapCreatePositionParams): string | null {
	const search = buildCreatePositionSearchParams(params);
	if (!search) return null;
	return `https://app.uniswap.org/positions/create/v4?${search.toString()}`;
}

/**
 * Version-aware Uniswap create position URL (V3 or V4).
 * Defaults to V4 when version is omitted for backward compatibility.
 */
export function getUniswapCreatePositionUrl(params: UniswapCreatePositionParams): string | null {
	const version = params.version ?? "V4";
	return version === "V3"
		? getUniswapV3CreatePositionUrl(params)
		: getUniswapV4CreatePositionUrl(params);
}
